import ReactMarkdown from "react-markdown";
import { ExternalLink } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { AnnouncementResponse } from "@/hooks/useAnnouncements";

interface Props {
  announcement: AnnouncementResponse | null;
  open: boolean;
  onClose: (acked: boolean) => void;
  /** When true, CTA + Got-it buttons are hidden (act-as read-only mode). */
  readOnly?: boolean;
  readOnlyReason?: string;
  /** Dry-run preview — render buttons but block navigation/ack. */
  previewMode?: boolean;
}

function isExternalUrl(url: string): boolean {
  return url.startsWith("https://");
}

export function AnnouncementDetailDialog({
  announcement,
  open,
  onClose,
  readOnly = false,
  readOnlyReason,
  previewMode = false,
}: Props) {
  const navigate = useNavigate();

  if (!announcement) return null;

  function handleCta() {
    if (!announcement || !announcement.cta_url) return;
    if (previewMode) {
      onClose(false);
      return;
    }
    if (isExternalUrl(announcement.cta_url)) {
      window.open(announcement.cta_url, "_blank", "noopener,noreferrer");
    } else {
      navigate({ to: announcement.cta_url });
    }
    onClose(true);
  }

  function handleGotIt() {
    if (previewMode) {
      onClose(false);
      return;
    }
    onClose(true);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => !v && onClose(false)}
    >
      <DialogContent className="max-h-[80vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>{announcement.title}</DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto pr-2 prose prose-sm dark:prose-invert">
          <ReactMarkdown>{announcement.body_md}</ReactMarkdown>
        </div>
        <DialogFooter className="flex-shrink-0 border-t pt-3">
          {readOnly ? (
            <div className="text-xs text-muted-foreground italic">
              {readOnlyReason ?? "Read-only."}
            </div>
          ) : (
            <>
              {announcement.cta_label && announcement.cta_url && (
                <Button onClick={handleCta}>
                  {announcement.cta_label}
                  {isExternalUrl(announcement.cta_url) && (
                    <ExternalLink className="size-3.5 ml-1.5" />
                  )}
                </Button>
              )}
              <Button variant="outline" onClick={handleGotIt}>
                Got it
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
